/**
 * ExportDialog - Export EV load to Site DNA
 *
 * Two modes:
 * - New project: Creates new Site DNA project with EV load
 * - Add to existing: Adds scenario to existing project
 */

import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormControl,
    FormLabel,
    Autocomplete,
    Box,
    Typography,
    CircularProgress,
    Alert,
} from '@mui/material';
import { useAuth } from '@vortx/shared-ui';
import * as evService from '@vortx/shared-ui/src/services/evProjectService';

/**
 * ExportDialog Component
 *
 * @param {Object} props
 * @param {boolean} props.open - Dialog open state
 * @param {Function} props.onClose - Close callback
 * @param {string} props.evProjectId - EV project to export
 * @param {string} props.evProjectName - EV project name for default naming
 * @param {Function} props.onSuccess - Success callback with result
 */
function ExportDialog({
    open,
    onClose,
    evProjectId,
    evProjectName = '',
    onSuccess,
}) {
    const { currentUser } = useAuth();
    const [mode, setMode] = useState('new_project');
    const [name, setName] = useState('');
    const [siteDNAProjects, setSiteDNAProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingProjects, setLoadingProjects] = useState(false);
    const [error, setError] = useState(null);

    // Reset state when dialog opens
    useEffect(() => {
        if (open) {
            setMode('new_project');
            setName(`${evProjectName} - EV Site`);
            setSelectedProject(null);
            setError(null);
        }
    }, [open, evProjectName]);

    // Load Site DNA projects when mode changes to add_scenario
    useEffect(() => {
        if (mode !== 'add_scenario' || !currentUser) return;

        const loadProjects = async () => {
            try {
                setLoadingProjects(true);
                const data = await evService.getSiteDNAProjects(currentUser);
                setSiteDNAProjects(data.projects || []);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoadingProjects(false);
            }
        };

        loadProjects();
    }, [mode, currentUser]);

    const handleExport = async () => {
        if (!currentUser || !evProjectId) return;

        // Validation
        if (mode === 'new_project' && !name.trim()) {
            setError('Project name is required');
            return;
        }
        if (mode === 'add_scenario' && !selectedProject) {
            setError('Please select a project');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const options = {
                mode,
                name: name.trim(),
                target_project_id: selectedProject?.id,
            };

            const result = await evService.exportToSiteDNA(evProjectId, options, currentUser);

            if (onSuccess) {
                onSuccess(result);
            }
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Export to Site DNA</DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Export the EV charging load profile to Site DNA for solar+storage optimization.
                </Typography>

                <FormControl component="fieldset" sx={{ mb: 3 }}>
                    <FormLabel>Export Mode</FormLabel>
                    <RadioGroup
                        value={mode}
                        onChange={(e) => setMode(e.target.value)}
                    >
                        <FormControlLabel
                            value="new_project"
                            control={<Radio />}
                            label="Create new Site DNA project"
                        />
                        <FormControlLabel
                            value="add_scenario"
                            control={<Radio />}
                            label="Add scenario to existing project"
                        />
                    </RadioGroup>
                </FormControl>

                {mode === 'new_project' && (
                    <TextField
                        label="Project Name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        fullWidth
                        sx={{ mb: 2 }}
                    />
                )}

                {mode === 'add_scenario' && (
                    <Box>
                        {loadingProjects ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={20} />
                                <Typography variant="body2">Loading projects...</Typography>
                            </Box>
                        ) : (
                            <>
                                <Autocomplete
                                    value={selectedProject}
                                    onChange={(e, newValue) => setSelectedProject(newValue)}
                                    options={siteDNAProjects}
                                    getOptionLabel={(option) => option.name}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Select Project"
                                            placeholder="Search projects..."
                                        />
                                    )}
                                    sx={{ mb: 2 }}
                                />
                                <TextField
                                    label="Scenario Name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    fullWidth
                                    helperText="Name for the new scenario"
                                />
                            </>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleExport}
                    disabled={loading}
                    startIcon={loading && <CircularProgress size={16} color="inherit" />}
                >
                    {loading ? 'Exporting...' : 'Export'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ExportDialog;
